import { ManageOfferingInputSubSection } from "features/ManageOfferings/modules/ManageOfferingInputSubSection/ManageOfferingInputSubSection";
import { WhitelabelFeatureCard } from "features/ManageOfferings/modules/OfferingPageSections/modules/WhitelabelFeatureCard/WhitelabelFeatureCard";
import React from "react";
import { validURLV2 } from "utils/validations";
import { DeviceManagementSubSection } from "../../DeviceManagement/DeviceManagementSubSection";
import { RequestVerificationKRA } from "../../SubSectionWrapper/modules/RequestVerificationKRA/RequestVerificationKRA";
import { KRAToggle } from "../modules/KRAToggle/KRAToggle";
import { SubscriptionGradePeriodToggle } from "../modules/SubscriptionGradePeriodToggle/SubscriptionGradePeriodToggle";
import { KYCTypeSubsection } from "features/ManageOfferings/modules/OfferingPageSections/modules/KYCTypeSubsection/KYCTypeSubsection";
import { ManageOfferingsLockBtn } from "features/ManageOfferings/modules/ManageOfferingsLockBtn/ManageOfferingsLockBtn";
import { openPricingPage } from "utils/Utils";
import { FEATURE_KEYS } from "./FeatureKeys.constants";
import { UPDATE_ADMIN_PANEL_SETTINGS } from "features/AdminPanel/constants/AdminPanel.api";
import { OFFERING_SUBSECTION_KEYS } from "features/ManageOfferings/modules/OfferingPageSections/constants/OfferingPageSections.constants";
import { PAGE_SECTIONS_KEYS } from "features/ManageOfferings/modules/OfferingPageSections/constants/OfferingPageSectionsKeys.constants";
import { cannotBeEmpty } from "common/validate";

export const GRACE_PERIOD_KEYS = {
  SUBSCRIPTION_GRACE_PERIOD: "subscription_grace_period",
  LISTING_APPLICABILTITY: "listing_applicability",
};

export const GRACE_PERIOD_MODAL_STORE_KEYS = {
  isGracePeriod: "isGracePeriod",
  gracePeriodDays: "gracePeriodDays",
  isToggled: "isToggled",
};

export const GRACE_PERIOD_APPLICABILTITY_KEYS = {
  ALL_LISTING: 1,
  NEW_LISTING: 2,
};

export const GRACE_PERIOD_APPLICABILTITY_OPTIONS = [
  {
    label: "Only apply to new offerings",
    value: GRACE_PERIOD_APPLICABILTITY_KEYS.NEW_LISTING,
  },
  {
    label: "Apply to all offerings (existing and new)",
    value: GRACE_PERIOD_APPLICABILTITY_KEYS.ALL_LISTING,
  },
];

export const KRA_WORKFLOW_ACCOUNT_CONFIG = {
  key: PAGE_SECTIONS_KEYS.IS_DIGIO_KYC_ENABLED,
  title: "Enable KRA Workflow",
  description:
    "Enable KRA flow for centralized KYC management. SEBI-mandated system for digitization and centralization of KYC records across securities industry.",
  customRightJSX: <KRAToggle subSectionKey={FEATURE_KEYS.KRA_FLOW} />,
  permissionKey: FEATURE_KEYS.KRA_FLOW,
  lockCustomRightJSX: (
    <ManageOfferingsLockBtn btnText="Upgrade Plan" action={openPricingPage} />
  ),
  customFooter: (
    <RequestVerificationKRA config={{ key: FEATURE_KEYS.KRA_FLOW }} />
  ),
  classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
};

export const FEATURE_CONFIG = [
  {
    title: "Add-To-Cart",
    description:
      "Let customers save your offering in their cart and enable multiple purchases at once.",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.ADD_TO_CART,
        title: "Enable Add-to-cart",
        description:
          "Toggle on to let customers add multiple offerings to their cart during booking.",
      },
    ],
  },
  {
    key: FEATURE_KEYS.IVR,
    title: "IVR for Failed Customer Transactions",
    description:
      "Activate automated phone support to enhance customer experience.",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.IVR,
        title: "Enable IVR",
        description:
          "Toggle on to activate IVR calling for failed or pending customer transactions.",
      },
    ],
  },
  {
    title: "Customer details updation",
    description:
      "Allow customer to update their email and phone number during offering purchase.",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.IS_CUSTOMER_DETAILS_UPDATION_ALLOWED,
        title: "Enable customers to update details",
        description:
          "Toggle on to let customers to update their phone number and email if there's a mismatch during booking.",
      },
    ],
  },
  {
    title: "Disable Customer Login",
    description:
      "Remove login access for your customers to keep them from accessing their bookings and purchase history.",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.TOGGLE_CUSTOMER_LOGIN,
        title: "Disable Customer Login",
        description:
          "When toggled on, your customers will no longer be able to log in anywhere on your website or access their past/upcoming bookings and purchase history.",
      },
    ],
  },
  {
    title: "Device Management",
    description:
      "Manage how customers login across devices and set limits if needed",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.DEVICE_MANAGEMENT,
        title: "Limit number of devices",
        customRightJSX: (
          <DeviceManagementSubSection
            subsectionKey={FEATURE_KEYS.DEVICE_MANAGEMENT}
          />
        ),
        hideSubsectionText: true,
      },
    ],
  },
  {
    title: "Grace Period for Subscriptions",
    description:
      "Let customers keep offering access after renewal date. Set grace period duration.",
    subSectionConfig: [
      {
        key: FEATURE_KEYS.SUBSCRIPTION_GRACE_PERIOD,
        title: "Enable grace period",
        description:
          "Toggle on to let customers keep access even if they haven’t renewed their subscription yet. You can choose how long the grace period lasts.",
        customRightJSX: (
          <SubscriptionGradePeriodToggle
            subSectionKey={FEATURE_KEYS.SUBSCRIPTION_GRACE_PERIOD}
          />
        ),
      },
    ],
  },
  {
    id: OFFERING_SUBSECTION_KEYS.VERIFICATION_AT_CHECKOUT,
    title: "Verification at checkout",
    description:
      "Show necessary ‘Terms and conditions’ for customers to view and acknowledge at checkout.",
    customWhitelabelLock: <WhitelabelFeatureCard />,
    subSectionConfig: [
      {
        key: FEATURE_KEYS.CUSTOM_TNC_LABEL,
        title: "Edit Terms & Condition statement",
        description:
          "The customer will see this text when they fill all their details and are about to proceed for payment. ",
        customRightJSX: (
          <ManageOfferingInputSubSection
            subSectionKey={FEATURE_KEYS.CUSTOM_TNC_LABEL}
            defaultValue=""
            placeholder="Edit statement"
            endPoint={UPDATE_ADMIN_PANEL_SETTINGS}
            validate={cannotBeEmpty}
          />
        ),
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        seeReferenceConfig: {
          id: FEATURE_KEYS.CUSTOM_TNC_LABEL,
          url: "assets/images/manage_offerings/tnc.png",
          height: "tw-h-[128px]",
          title: "Edit Terms & Condition statement",
          description:
            "The customer will be able to view this by clicking on the info icon at the end of the above text.",
        },
      },
      {
        key: FEATURE_KEYS.CUSTOM_TNC,
        title: "Add Terms & Condition",
        description:
          "The customer will be able to view this by clicking on the info icon at the end of the above text.",
        customRightJSX: (
          <ManageOfferingInputSubSection
            subSectionKey={FEATURE_KEYS.CUSTOM_TNC}
            defaultValue=""
            placeholder="Add Terms & Condition"
            endPoint={UPDATE_ADMIN_PANEL_SETTINGS}
            validate={(value) => {
              if (!value) return null;
              if (!validURLV2(value)) {
                return "Please enter a valid URL";
              }
              return null;
            }}
          />
        ),
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        seeReferenceConfig: {
          id: FEATURE_KEYS.CUSTOM_TNC,
          url: "assets/images/manage_offerings/tnc.png",
          height: "tw-h-[128px]",
          title: "Add Terms & Condition",
          description:
            "The customer will be able to view this by clicking on the info icon at the end of the above text",
        },
      },
      {
        key: FEATURE_KEYS.KYC_TYPE,
        title: "KYC Verification",
        description:
          "Choose the method used to verify customer identity for KYC compliance.",
        customRightJSX: (
          <KYCTypeSubsection
            subSectionKey={FEATURE_KEYS.KYC_TYPE}
            endPoint={UPDATE_ADMIN_PANEL_SETTINGS}
          />
        ),
        permissionKey: FEATURE_KEYS.KRA_FLOW,
        lockCustomRightJSX: (
          <ManageOfferingsLockBtn
            btnText="Upgrade Plan"
            action={openPricingPage}
          />
        ),
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
      },
    ],
  },
];

export const FEATURE_SECTION_KEY = "features";
